// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMantineTheme } from '@mantine/core';
import { Anchor, Badge, Box, Button, Center, Checkbox, Container, PasswordInput, Stack, Text, TextInput, Title } from '@/components/ui';
import { IconUser, IconMail, IconLock, IconArrowLeft, IconBolt } from '@tabler/icons-react';
import { FadeIn, SlideIn } from '@/components/animations';
import { NeonCard, SocialLoginButtons, PasswordStrength } from '../components';

export default function RegisterDarkMode() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const theme = useMantineTheme();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Register attempt:', { name, email, password });
  };

  const inputStyles = {
    input: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      borderColor: 'rgba(191, 0, 255, 0.3)',
      color: 'white',
      '&:focus': {
        borderColor: '#bf00ff',
        boxShadow: '0 0 10px rgba(191, 0, 255, 0.3)',
      },
    },
    label: { color: '#bf00ff' },
  };

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a0a0f 0%, #1a0a2e 50%, #0f0a1a 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Grid background */}
      <Box
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(191, 0, 255, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(191, 0, 255, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          pointerEvents: 'none',
        }}
      />

      {/* Glow effects */}
      <Box
        style={{
          position: 'absolute',
          top: '10%',
          right: '15%',
          width: 300,
          height: 300,
          background: 'radial-gradient(circle, rgba(191, 0, 255, 0.15) 0%, transparent 70%)',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />
      <Box
        style={{
          position: 'absolute',
          bottom: '15%',
          left: '10%',
          width: 250,
          height: 250,
          background: 'radial-gradient(circle, rgba(255, 0, 255, 0.1) 0%, transparent 70%)',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />

      <Center mih="100vh" p="md" style={{ position: 'relative', zIndex: 1 }}>
        <Container size="xs" w="100%">
          <SlideIn direction="up" delay={100}>
            <NeonCard color="purple" animated glass>
              <Stack gap="lg">
                {/* Back button */}
                <Button
                  component={Link}
                  to="/demo/auth-pages/register"
                  variant="subtle"
                  size="xs"
                  leftSection={<IconArrowLeft size={14} />}
                  w="fit-content"
                  c="violet"
                >
                  Back to variants
                </Button>

                {/* Header */}
                <FadeIn delay={200}>
                  <Stack align="center" gap="xs">
                    <Badge
                      leftSection={<IconBolt size={12} />}
                      variant="filled"
                      color="violet"
                      size="lg"
                      style={{
                        boxShadow: '0 0 20px rgba(191, 0, 255, 0.5)',
                      }}
                    >
                      Cyberpunk Register
                    </Badge>
                    <Title order={2} ta="center" mt="md" c="white">
                      CREATE IDENTITY
                    </Title>
                    <Text size="sm" ta="center" c="dimmed">
                      Initialize new user protocol
                    </Text>
                  </Stack>
                </FadeIn>

                {/* Form */}
                <form onSubmit={handleSubmit}>
                  <Stack gap="md">
                    <FadeIn delay={250}>
                      <TextInput
                        label="Username"
                        placeholder="neo_user"
                        leftSection={<IconUser size={16} />}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        size="md"
                        radius="md"
                        styles={inputStyles}
                      />
                    </FadeIn>

                    <FadeIn delay={300}>
                      <TextInput
                        label="Email"
                        placeholder="user@matrix.net"
                        leftSection={<IconMail size={16} />}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        size="md"
                        radius="md"
                        styles={inputStyles}
                      />
                    </FadeIn>

                    <FadeIn delay={350}>
                      <div>
                        <PasswordInput
                          label="Password"
                          placeholder="••••••••"
                          leftSection={<IconLock size={16} />}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          size="md"
                          radius="md"
                          styles={inputStyles}
                        />
                        {password && <PasswordStrength password={password} variant="bar" />}
                      </div>
                    </FadeIn>

                    <FadeIn delay={400}>
                      <PasswordInput
                        label="Confirm Password"
                        placeholder="••••••••"
                        leftSection={<IconLock size={16} />}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        size="md"
                        radius="md"
                        styles={inputStyles}
                        error={confirmPassword && password !== confirmPassword ? "Mismatch detected" : undefined}
                      />
                    </FadeIn>

                    <FadeIn delay={450}>
                      <Checkbox
                        label={
                          <Text size="sm" c="dimmed">
                            Accept <Anchor size="sm" c="violet">Protocol Terms</Anchor>
                          </Text>
                        }
                        checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.currentTarget.checked)}
                        styles={{
                          label: { color: 'white' },
                        }}
                      />
                    </FadeIn>

                    <FadeIn delay={500}>
                      <Button
                        type="submit"
                        fullWidth
                        size="md"
                        radius="md"
                        color="violet"
                        variant="filled"
                        disabled={!acceptTerms}
                        style={{
                          boxShadow: '0 0 20px rgba(191, 0, 255, 0.4)',
                        }}
                      >
                        INITIALIZE ACCOUNT
                      </Button>
                    </FadeIn>
                  </Stack>
                </form>

                {/* Social Login */}
                <FadeIn delay={550}>
                  <SocialLoginButtons
                    providers={['google', 'github']}
                    layout="horizontal"
                    variant="outline"
                    dividerLabel="or link identity"
                  />
                </FadeIn>

                {/* Login Link */}
                <FadeIn delay={600}>
                  <Text ta="center" size="sm" c="dimmed">
                    Existing user?{' '}
                    <Anchor component={Link} to="/demo/auth-pages/login/neon" c="violet" fw={500}>
                      Access terminal
                    </Anchor>
                  </Text>
                </FadeIn>
              </Stack>
            </NeonCard>
          </SlideIn>
        </Container>
      </Center>
    </Box>
  );
}
